import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';

const endpoint = 'ums/v1/cms/exclusive-deals/suppliers';
export const updateDealProductEndpointIdentifier = `${endpoint}/:supplierId/products/:productId`;

// Mirrors BE UpdateProductOverrideDto — the product-addressed counterpart of the
// deal-scoped product-override route, for grid rows that have no deal yet.
export interface UpdateDealProductBody {
  nameEn?: string;
  brandEn?: string;
  defaultUnit?: string;
  defaultPrice?: number;
  specs?: string;
  imageUrl?: string;
  origin?: string;
  state?: string;
}

type TData = Record<string, any>;
type TError = AxiosError;
type TVariables = { supplierId: number; productId: number; body: UpdateDealProductBody };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * Update a supplier-level product from the exclusive-deals grid's inline edit flow
 * (rows without a persisted deal, e.g. right after inline product creation).
 */
export function useUpdateDealProductApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ supplierId, productId, body }: TVariables) => {
    try {
      const { data } = await http.put(`${endpoint}/${supplierId}/products/${productId}`, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [updateDealProductEndpointIdentifier],
    onSuccess: () => {
      // Refresh supplier product dropdowns so edited fields autofill correctly on re-pick.
      queryClient.invalidateQueries(['ums/v1/products']);
      toast({ description: 'Product information has been updated successfully', status: 'success' });
    },
    ...options,
  });
}
